import DOMPurify from 'isomorphic-dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - The HTML content to sanitize
 * @param {Object} config - Optional DOMPurify configuration
 * @returns {string} - Sanitized HTML content
 */
export const sanitizeHTML = (html, config = {}) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Default configuration for safe HTML
  const defaultConfig = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'i', 'b', 'span', 'div', 'ul', 'ol', 'li',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre', 'a'
    ],
    ALLOWED_ATTR: [
      'class', 'id', 'style', 'href', 'target', 'rel', 'title', 'alt'
    ],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false,
    RETURN_TRUSTED_TYPE: false,
    FORCE_BODY: false,
    SANITIZE_DOM: true,
    WHOLE_DOCUMENT: false,
    RETURN_TRUSTED_TYPE: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'onreset', 'onselect', 'onunload', 'onabort', 'onbeforeunload', 'onerror', 'onhashchange', 'onmessage', 'onoffline', 'ononline', 'onpagehide', 'onpageshow', 'onpopstate', 'onresize', 'onstorage', 'oncontextmenu', 'onkeydown', 'onkeypress', 'onkeyup', 'onmousedown', 'onmousemove', 'onmouseout', 'onmouseup', 'onwheel', 'oncopy', 'oncut', 'onpaste', 'onbeforecopy', 'onbeforecut', 'onbeforepaste', 'onselectstart', 'onselectionchange', 'onbeforeinput', 'oninput', 'oninvalid', 'onreset', 'onsearch', 'onselect', 'onchange', 'onsubmit', 'onreset', 'oninvalid', 'oninput', 'onbeforeinput', 'onbeforeunload', 'onerror', 'onhashchange', 'onmessage', 'onoffline', 'ononline', 'onpagehide', 'onpageshow', 'onpopstate', 'onresize', 'onstorage', 'oncontextmenu', 'onkeydown', 'onkeypress', 'onkeyup', 'onmousedown', 'onmousemove', 'onmouseout', 'onmouseup', 'onwheel', 'oncopy', 'oncut', 'onpaste', 'onbeforecopy', 'onbeforecut', 'onbeforepaste', 'onselectstart', 'onselectionchange', 'onbeforeinput', 'oninput', 'oninvalid', 'onreset', 'onsearch', 'onselect', 'onchange', 'onsubmit', 'onreset', 'oninvalid', 'oninput', 'onbeforeinput']
  };

  // Merge custom config with defaults
  const finalConfig = { ...defaultConfig, ...config };

  try {
    return DOMPurify.sanitize(html, finalConfig);
  } catch (error) {
    console.error('DOMPurify sanitization error:', error);
    return '';
  }
};

/**
 * Sanitize plain text content (no HTML)
 * @param {string} text - The text content to sanitize
 * @returns {string} - Sanitized text content
 */
export const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Remove any HTML tags and encode special characters
  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&/g, '&amp;') // Encode ampersands
    .replace(/</g, '&lt;') // Encode less than
    .replace(/>/g, '&gt;') // Encode greater than
    .replace(/"/g, '&quot;') // Encode double quotes
    .replace(/\//g, '&#x2F;'); // Encode forward slash
};

/**
 * Sanitize user input for display
 * @param {string} input - User input to sanitize
 * @param {boolean} allowHTML - Whether to allow HTML tags (default: false)
 * @returns {string} - Sanitized input
 */
export const sanitizeUserInput = (input, allowHTML = false) => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  if (allowHTML) {
    return sanitizeHTML(input);
  } else {
    return sanitizeText(input);
  }
};

/**
 * Validate and sanitize form data
 * @param {Object} formData - Form data object
 * @param {Object} schema - Validation schema
 * @returns {Object} - Sanitized form data
 */
export const sanitizeFormData = (formData, schema = {}) => {
  const sanitized = {};

  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === 'string') {
      // Check if this field should allow HTML based on schema
      const allowHTML = schema[key]?.allowHTML || false;
      sanitized[key] = sanitizeUserInput(value, allowHTML);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Create a React component wrapper for dangerouslySetInnerHTML with sanitization
 * @param {string} html - HTML content to render
 * @param {Object} config - DOMPurify configuration
 * @returns {Object} - React dangerouslySetInnerHTML object
 */
export const createSanitizedHTML = (html, config = {}) => {
  const sanitized = sanitizeHTML(html, config);
  return { __html: sanitized };
};

/**
 * Sanitize chat text for display (no HTML, but do NOT encode single quotes)
 * @param {string} text - The chat text to sanitize
 * @returns {string} - Sanitized chat text
 */
export const sanitizeChatText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\//g, '&#x2F;'); // Do NOT encode single quotes
};

/**
 * Sanitize yes/no text for display (no HTML, but do NOT encode single quotes)
 * @param {string} text - The yes/no text to sanitize
 * @returns {string} - Sanitized yes/no text
 */
export const sanitizeYesNoText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\//g, '&#x2F;'); // Do NOT encode single quotes
}; 